import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Opportunity } from '../../types';
import {
  getOpportunities,
  getOpportunityById,
  createOpportunity as createOpportunityAPI,
  updateOpportunity as updateOpportunityAPI,
  deleteOpportunity as deleteOpportunityAPI,
  CreateOpportunityRequest,
} from '../../services/opportunity-service';

interface OpportunityState {
  items: Opportunity[];
  selectedOpportunity: Opportunity | null;
  loading: boolean;
  error: string | null;
}

const initialState: OpportunityState = {
  items: [],
  selectedOpportunity: null,
  loading: false,
  error: null,
};

// Fetch all opportunities
export const fetchOpportunities = createAsyncThunk(
  'opportunities/fetchOpportunities',
  async (_, { rejectWithValue }) => {
    try {
      return await getOpportunities();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// Fetch a single opportunity by ID
export const fetchOpportunityById = createAsyncThunk(
  'opportunities/fetchOpportunityById',
  async (id: string, { rejectWithValue }) => {
    try {
      return await getOpportunityById(id);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// Create a new opportunity
export const createOpportunity = createAsyncThunk(
  'opportunities/createOpportunity',
  async (data: CreateOpportunityRequest, { rejectWithValue }) => {
    try {
      return await createOpportunityAPI(data);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// Update an existing opportunity
export const updateOpportunity = createAsyncThunk(
  'opportunities/updateOpportunity',
  async (
    { id, data }: { id: string; data: Partial<CreateOpportunityRequest> },
    { rejectWithValue }
  ) => {
    try {
      return await updateOpportunityAPI(id, data);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// Delete an opportunity
export const deleteOpportunity = createAsyncThunk(
  'opportunities/deleteOpportunity',
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteOpportunityAPI(id);
      return id;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

const opportunitySlice = createSlice({
  name: 'opportunities',
  initialState,
  reducers: {
    selectOpportunity: (state, action: PayloadAction<Opportunity>) => {
      state.selectedOpportunity = action.payload;
    },
    clearSelectedOpportunity: state => {
      state.selectedOpportunity = null;
    },
    markChatsAsRead: (state, action: PayloadAction<string>) => {
      const opportunityId = action.payload;
      const opportunity = state.items.find(item => item.id === opportunityId);
      if (opportunity && opportunity.unread_chats) {
        opportunity.unread_chats = false;
      }
    },
  },
  extraReducers: builder => {
    builder
      // Fetch opportunities
      .addCase(fetchOpportunities.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOpportunities.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchOpportunities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch opportunity by ID
      .addCase(fetchOpportunityById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOpportunityById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOpportunity = action.payload;
      })
      .addCase(fetchOpportunityById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create opportunity
      .addCase(createOpportunity.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOpportunity.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(createOpportunity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update opportunity
      .addCase(updateOpportunity.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOpportunity.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.selectedOpportunity?.id === action.payload.id) {
          state.selectedOpportunity = action.payload;
        }
      })
      .addCase(updateOpportunity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete opportunity
      .addCase(deleteOpportunity.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOpportunity.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item.id !== action.payload);
        if (state.selectedOpportunity?.id === action.payload) {
          state.selectedOpportunity = null;
        }
      })
      .addCase(deleteOpportunity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { selectOpportunity, clearSelectedOpportunity, markChatsAsRead } =
  opportunitySlice.actions;
export default opportunitySlice.reducer;
